import type * as API from "../data/api-types";
import { objectManifest } from "../data/object-manifest.generated";

/** UI ordering for the funnel — mirrors
 * `sales_leads_module.models.lead.LeadStage`'s declaration order (module
 * spec §16 item 3). Not business logic: purely display convention. */
export const LEAD_STAGES: readonly API.LeadStage[] = [
  "cold",
  "warm",
  "hot",
  "contact_made",
  "quoted",
  "follow_up",
  "first_order",
];

/** The stage labels as the backend writes them, the one place
 * (`sales_leads_module.models.lead.LEAD_STAGE_LABELS`): read from the
 * generated object manifest's stage filter on the lead list. */
function stageLabelsFromManifest(): Record<API.LeadStage, string> {
  const lead = objectManifest.objects.find((o) => o.id === "sales_leads.lead");
  const stage = lead?.list?.filters.find((f) => f.param === "stage");
  const labels = new Map(stage?.options ?? []);
  return Object.fromEntries(
    LEAD_STAGES.map((value) => [value, labels.get(value) ?? value]),
  ) as Record<API.LeadStage, string>;
}

export const LEAD_STAGE_LABELS: Record<API.LeadStage, string> =
  stageLabelsFromManifest();

/** Stages a rep may pick directly from the stage selector. `first_order`
 * is excluded — `advance_lead_stage` always rejects it as a direct target
 * (`LeadStageNotDirectlySettableError`); conversion (`convertLeadToCustomer`)
 * is the only path there. */
export const DIRECTLY_SETTABLE_LEAD_STAGES: readonly API.LeadStage[] =
  LEAD_STAGES.filter((stage) => stage !== "first_order");

/** Interest-tag options shown on the create-lead form and the lead detail
 * page — every value of the `MarketingCategory` enum. Display
 * convention shared between the two call sites, not business logic. */
export const INTEREST_OPTIONS: readonly API.MarketingCategory[] = [
  "print",
  "display",
  "mailing",
  "merch",
  "clothing",
  "creative",
  "other",
];

export interface LeadAddressFormFields {
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  country: string;
}

/** Builds a `LeadAddress` from raw address-form input, shared between
 * `CreateLeadModal` and `LeadDetailPage`'s inline address editor.
 *
 * Every `LeadAddress` field is independently optional server-side
 * (`LeadAddressSchema` — a postcode-only address is legitimate, proven by
 * the backend's own route tests), so this nulls each field independently
 * rather than requiring a fixed "complete address" shape before sending
 * anything. Returns `null` only when every field is blank.
 *
 * That `null` matters beyond "no address": on `update_lead`, `address:
 * null` is the explicit *clear* sentinel (only an omitted key means
 * "unchanged" — see `UpdateLeadRequest`'s docstring), so a form with some
 * fields filled in must never collapse to `null` — that would silently
 * wipe a lead's stored address instead of sending the partial address for
 * the backend's own validation to flag (e.g. at convert-time).
 * `create_lead` has no clear semantics to worry about, but the same
 * per-field shape is correct there too — it's just "no address" either
 * way. */
export function buildLeadAddressFromForm(
  fields: LeadAddressFormFields,
): API.LeadAddress | null {
  const address = {
    address_line1: fields.addressLine1.trim() || null,
    address_line2: fields.addressLine2.trim() || null,
    city: fields.city.trim() || null,
    postcode: fields.postcode.trim() || null,
    country: fields.country.trim() || null,
  };
  const isBlank = Object.values(address).every((value) => value === null);
  return isBlank ? null : address;
}
